import { useCallback, useEffect, useState } from 'react'
import { Loader2, Pencil, Plus, ShieldCheck, Trash2 } from 'lucide-react'
import type { StaticIpAssignmentDto, StaticIpDto, StaticIpsResponse } from '@apna/shared'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'
import { AssignStaticIpDialog } from './AssignStaticIpDialog'
import { StaticIpDialog, type StaticIpForm } from './StaticIpDialog'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'

const th = 'px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'

/** A lease that has run out, or is about to, said in words rather than a date. */
function validity(ip: StaticIpDto): { text: string; tone: string } {
  if (ip.daysLeft === null) return { text: 'No end date', tone: 'text-muted-foreground' }
  if (ip.daysLeft < 0) return { text: `Expired ${Math.abs(ip.daysLeft)}d ago`, tone: 'text-destructive' }
  if (ip.daysLeft <= 7) return { text: `${ip.daysLeft}d left`, tone: 'text-warning' }
  return { text: `${ip.daysLeft}d left`, tone: 'text-muted-foreground' }
}

const Pill = ({ tone, children }: { tone: string; children: React.ReactNode }) => (
  <span className={cn('inline-flex rounded px-2 py-0.5 text-[10px] font-semibold', tone)}>{children}</span>
)

/**
 * Every static IP, and which broker accounts leave from each.
 *
 * VALIDITY IS A COLUMN BECAUSE A LAPSED LEASE IS AN OUTAGE. A rented address
 * stops being ours when the rental ends, and the first symptom is every order
 * on that account refused for an IP mismatch the broker will not explain.
 * Seeing "expired 139d ago" on a screen is cheaper than discovering it from a
 * rejected order.
 *
 * VERIFIED means the last check SAW the expected address — not merely that a
 * check ran. An endpoint with nothing recorded to compare against can never
 * be verified, and says so rather than showing a hopeful tick.
 */
export function StaticIpList() {
  const [data, setData] = useState<StaticIpsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [assigning, setAssigning] = useState<StaticIpDto | null>(null)
  const [editing, setEditing] = useState<StaticIpDto | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [deleting, setDeleting] = useState<StaticIpDto | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setData(await api.get<StaticIpsResponse>('/broker/static-ips'))
      setError(null)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not load static IPs.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const save = async (connectionIds: string[]) => {
    if (!assigning) return
    await api.put(`/broker/static-ips/${assigning.id}/assignment`, { connectionIds })
    await load()
  }

  /*
   * A blank secret means KEEP on an edit, so those fields are sent ONLY when
   * typed into. The server reads an absent key as "leave it alone" — see
   * `credentialPatch`. Sending '' instead would strip the credentials off
   * every proxy anyone opened and saved without retyping them.
   */
  const saveForm = async (form: StaticIpForm, editingId: string | null) => {
    const body = {
      label: form.label,
      kind: form.kind,
      host: form.kind === 'DIRECT' ? null : form.host || null,
      port: form.kind === 'DIRECT' ? null : Number(form.port) || null,
      expectedExitIp: form.expectedExitIp || null,
      validityEndsAt: form.validityEndsAt || null,
      source: form.source,
      isActive: form.isActive,
      ...(form.username ? { username: form.username } : {}),
      ...(form.password ? { password: form.password } : {}),
    }
    if (editingId) await api.put(`/broker/static-ips/${editingId}`, body)
    else await api.post('/broker/static-ips', body)
    await load()
  }

  const remove = async () => {
    if (!deleting) return
    await api.del(`/broker/static-ips/${deleting.id}`)
    setDeleting(null)
    await load()
  }

  const endpoints = data?.endpoints ?? []
  const assignments: StaticIpAssignmentDto[] = data?.assignments ?? []

  return (
    <div className="rounded-xl border bg-card shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
        <div>
          <h3 className="text-[13px] font-semibold">Static IPs</h3>
          <p className="text-[11px] text-muted-foreground">
            The addresses your brokers whitelist. An account with none assigned sends from the platform address.
          </p>
        </div>
        <button type="button" data-testid="sip-add"
          onClick={() => { setEditing(null); setFormOpen(true) }}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-[12px] font-semibold text-primary-foreground">
          <Plus size={14} /> Add Static IP
        </button>
      </div>
      <Table containerClassName="rounded-xl" className="min-w-[860px]">
        <TableHeader>
          <TableRow className="border-b bg-muted/40 hover:bg-muted/40">
            <TableHead className={cn(th, 'text-left')}>Static IP</TableHead>
            <TableHead className={cn(th, 'text-left')}>Assigned</TableHead>
            <TableHead className={th}>Status</TableHead>
            <TableHead className={th}>Verification</TableHead>
            <TableHead className={th}>Validity</TableHead>
            <TableHead className={cn(th, 'text-right')}>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading && (
            <TableRow><TableCell colSpan={6} className="px-4 py-10 text-center">
              <Loader2 size={18} className="mx-auto animate-spin text-muted-foreground" />
            </TableCell></TableRow>
          )}

          {!loading && error && (
            <TableRow><TableCell colSpan={6} className="px-4 py-10 text-center text-[12px] text-destructive">{error}</TableCell></TableRow>
          )}

          {!loading && !error && endpoints.length === 0 && (
            <TableRow><TableCell colSpan={6} className="px-4 py-10 text-center text-[12px] text-muted-foreground">
              No static IPs yet. Accounts without one send orders from the platform address.
            </TableCell></TableRow>
          )}

          {!loading && !error && endpoints.map((ip) => {
            const v = validity(ip)
            return (
              <TableRow key={ip.id} data-testid={`static-ip-${ip.id}`}>
                <TableCell className="px-4 py-3 text-left">
                  <div className="num text-[13px] font-medium">{ip.expectedExitIp ?? '—'}</div>
                  <div className="text-[11px] text-muted-foreground">{ip.label}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {ip.kind === 'DIRECT' ? 'Direct' : `${ip.kind === 'SOCKS5' ? 'SOCKS5' : 'HTTP'} · ${ip.host ?? '?'}:${ip.port ?? '?'}`}
                  </div>
                </TableCell>

                <TableCell className="px-4 py-3 text-left">
                  {ip.assigned.length === 0 ? (
                    <span className="text-[11px] text-muted-foreground">Not assigned</span>
                  ) : (
                    <span className="flex flex-wrap gap-1">
                      {ip.assigned.map((a) => (
                        <Pill key={a.connectionId} tone="bg-secondary text-foreground/80">{a.label}</Pill>
                      ))}
                    </span>
                  )}
                </TableCell>

                <TableCell className="px-4 py-3 text-center">
                  <Pill tone={ip.isActive ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'}>
                    {ip.isActive ? 'Active' : 'Inactive'}
                  </Pill>
                </TableCell>

                <TableCell className="px-4 py-3 text-center">
                  {ip.verified
                    ? <Pill tone="bg-success/10 text-success">Verified</Pill>
                    : <Pill tone="bg-destructive/10 text-destructive">{ip.lastVerifiedAt ? 'Mismatch' : 'Unverified'}</Pill>}
                  {ip.lastVerifiedAt && (
                    <div className="mt-0.5 text-[10px] text-muted-foreground">
                      {new Date(ip.lastVerifiedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </div>
                  )}
                </TableCell>

                <TableCell className={cn('px-4 py-3 text-center text-[11px]', v.tone)}>{v.text}</TableCell>

                <TableCell className="px-4 py-3 text-right">
                  <span className="inline-flex items-center gap-1.5">
                    <button type="button" onClick={() => setAssigning(ip)} data-testid={`assign-open-${ip.id}`}
                      className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-[11px] font-medium hover:bg-muted/60">
                      <ShieldCheck size={13} /> Assign
                    </button>
                    <button type="button" aria-label={`Edit ${ip.label}`} data-testid={`sip-edit-${ip.id}`}
                      onClick={() => { setEditing(ip); setFormOpen(true) }}
                      className="rounded-md border border-border p-1.5 hover:bg-muted/60">
                      <Pencil size={13} />
                    </button>
                    <button type="button" aria-label={`Delete ${ip.label}`} data-testid={`sip-delete-${ip.id}`}
                      onClick={() => setDeleting(ip)}
                      className="rounded-md border border-border p-1.5 text-destructive hover:bg-destructive/10">
                      <Trash2 size={13} />
                    </button>
                  </span>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      <AssignStaticIpDialog
        endpoint={assigning}
        assignments={assignments}
        onClose={() => setAssigning(null)}
        onSave={save}
      />

      <StaticIpDialog
        open={formOpen}
        editing={editing}
        onClose={() => { setFormOpen(false); setEditing(null) }}
        onSave={saveForm}
      />

      {/*
        Names the accounts, because deleting is not a tidy-up: every account
        this endpoint carries starts sending from the platform address on its
        next order, and if the broker has not whitelisted that, they are
        refused.
      */}
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => { if (!o) setDeleting(null) }}
        title={`Delete ${deleting?.label ?? ''}?`}
        description={
          deleting && deleting.assigned.length > 0
            ? `${deleting.assigned.map((a) => a.label).join(', ')} will go back to the platform address on the next order.`
            : 'No accounts are assigned to it.'
        }
        confirmLabel="Delete"
        destructive
        onConfirm={() => void remove()}
      />
    </div>
  )
}
